import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import DispatchSmsModal from './DispatchSmsModal';
import DispatchToolPicker from './DispatchToolPicker';
import { ToolAmendmentModal, ToolAmendments } from './ToolAmendments';
import { printToolSheet, LoadedToolsModal } from './DispatchLoadedTools';
import { CancelJobModal, RebookJobModal } from './CancelJobModal';
import { Popover, PopoverTrigger, PopoverContent } from './ui/popover';
import { Calendar as CalendarComp } from './ui/calendar';
import { format, parseISO } from 'date-fns';
import { UnpackedBoxesBanner } from './UnpackedBoxesBanner';
import { SkillsNeeded } from './dispatch/SkillsNeeded';
import { RateJobModal } from './skills/Feedback';
import {
  Send, Plus, ArrowLeft, Loader2, X, Truck, Wrench, Users, Calendar,
  CheckCircle2, XCircle, Clock, MapPin, FileText, Pencil, Trash2, Search,
  Hourglass, AlertTriangle, MessageSquare, Printer, ClipboardCheck, Phone, Globe, HelpCircle, Star } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const APPROVE_ROLES = ['super_admin', 'admin', 'office_admin', 'dispatch'];

// Format a busy booking date range compactly: "Jul 20–22", "Jul 28 – Aug 2", "Jul 20"
const fmtRange = (s, e) => {
  try {
    const sd = parseISO(s);
    const ed = e ? parseISO(e) : sd;
    if (!e || s === e) return format(sd, 'MMM d');
    return format(sd, 'MMM') === format(ed, 'MMM')
      ? `${format(sd, 'MMM d')}–${format(ed, 'd')}`
      : `${format(sd, 'MMM d')} – ${format(ed, 'MMM d')}`;
  } catch { return ''; }
};
// Label shown beside a greyed-out (already-scheduled) unit or crew member
const busyLabel = (b) => (b ? `WO ${b.wo || '—'} · ${fmtRange(b.start, b.end)}` : '');
// Label beside someone who is already on THIS job (usually the block Blair put on the dispatch sheet) — not a conflict
const sameJobLabel = (b) => (b ? `on this job${b.source === 'sheet' ? ' (sheet)' : ''} · ${fmtRange(b.start, b.end)}` : '');


const STATUS_STYLES = {
  pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700 border-amber-200', Icon: Clock },
  approved: { label: 'Approved', cls: 'bg-green-100 text-green-700 border-green-200', Icon: CheckCircle2 },
  declined: { label: 'Declined', cls: 'bg-red-100 text-red-700 border-red-200', Icon: XCircle },
  cancelled: { label: 'Cancelled', cls: 'bg-red-50 text-red-600 border-red-300', Icon: XCircle },
};

const todayISO = () => new Date().toISOString().slice(0, 10);

const fmtDate = (d) => {
  if (!d) return '—';
  try {
    return new Date(`${d}T12:00:00`).toLocaleDateString('en-US', {
      timeZone: 'America/Toronto', month: 'short', day: 'numeric', year: 'numeric',
    });
  } catch { return d; }
};

const StatusBadge = ({ status }) => {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const { Icon } = s;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${s.cls}`} data-testid={`dr-status-${status}`}>
      <Icon size={13} /> {s.label}
    </span>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-[#1e3a5f] mb-1 uppercase tracking-wide">{label}</label>
    {children}
  </div>
);

// ── Dispatch delivery / reply status chip ───────────────────────────
const DispatchStatusChip = ({ rec }) => {
  const status = rec.status;
  const isPm = rec.role === 'pm';
  const isFyi = isPm || rec.role === 'dispatch';
  let Icon = Hourglass, cls = 'bg-amber-50 text-amber-700 border-amber-200', label = 'Awaiting reply';
  if (status === 'failed') {
    Icon = AlertTriangle; cls = 'bg-red-50 text-red-700 border-red-200';
    label = rec.error ? `Failed: ${rec.error.length > 70 ? rec.error.slice(0, 70) + '…' : rec.error}` : 'Failed to send';
  } else if (status === 'confirmed') {
    Icon = CheckCircle2; cls = 'bg-green-50 text-green-700 border-green-200'; label = 'Confirmed';
  } else if (status === 'declined') {
    Icon = XCircle; cls = 'bg-red-50 text-red-700 border-red-200'; label = 'Declined';
  } else if (status === 'notified' || (isFyi && (status === 'sent' || status === 'pending'))) {
    Icon = MessageSquare; cls = 'bg-gray-50 text-gray-600 border-gray-200'; label = 'In chat — no reply needed';
  } else if (status === 'sent') {
    Icon = Hourglass; cls = 'bg-amber-50 text-amber-700 border-amber-200'; label = 'Awaiting reply';
  } else if (status === 'pending') {
    Icon = Hourglass; cls = 'bg-gray-50 text-gray-500 border-gray-200'; label = 'Queued';
  }
  return (
    <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cls}`} title={rec.error || ''} data-testid={`dr-recipient-status-${rec.crew_member_id || rec.crew_member_phone}`}>
      <Icon size={13} className={status === 'sent' && !isFyi ? 'animate-pulse' : ''} />
      <span className="font-semibold">{rec.crew_member_name || 'Recipient'}</span>
      <span className="opacity-70">· {isPm ? 'PM' : rec.role === 'dispatch' ? 'Dispatch' : 'Crew'}</span>
      <span className="opacity-90">— {label}</span>
    </div>
  );
};

const inputCls = 'w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent outline-none bg-white';

// ── Crew multi-select ───────────────────────────────────────────────
const CrewPicker = ({ options, value, onChange, busy, sameJob, vacations, testid, jobStart, jobEnd, tbd }) => {
  const [q, setQ] = useState('');
  const [focused, setFocused] = useState(false);
  const [dateEditId, setDateEditId] = useState(null);
  const tbdOn = !!tbd && (tbd.count || 0) > 0;
  const showTbdOpt = !!tbd && !tbdOn && (!q || 'tbd to be determined later unknown'.includes(q.toLowerCase()));
  const selectedIds = new Set((value || []).map((v) => v.id));
  const filtered = (options || []).filter(
    (o) => !selectedIds.has(o.id) && o.name.toLowerCase().includes(q.toLowerCase())
  );
  const add = (o) => {
    onChange([...(value || []), { id: o.id, name: o.name }]);
    const vac = vacations?.[o.id];
    if (vac) toast.warning(`${o.name} has vacation booked off — ${vac.label}`, { duration: 8000 });
    setQ('');
  };
  const remove = (id) => {
    onChange((value || []).filter((v) => v.id !== id));
    if (dateEditId === id) setDateEditId(null);
  };
  const fmtShort = (d) => { try { return format(parseISO(d), 'MMM d'); } catch { return d || ''; } };
  const setMemberDate = (id, field, val) => {
    const jobDefault = field === 'start_date' ? jobStart : jobEnd;
    onChange((value || []).map((v) => {
      if (v.id !== id) return v;
      const nv = { ...v };
      if (!val || val === jobDefault) delete nv[field];
      else nv[field] = val;
      return nv;
    }));
  };
  const clearMemberDates = (id) => {
    onChange((value || []).map((v) => {
      if (v.id !== id) return v;
      const { start_date, end_date, ...rest } = v;
      return rest;
    }));
    setDateEditId(null);
  };
  const editingMember = dateEditId ? (value || []).find((x) => x.id === dateEditId) : null;
  return (
    <div data-testid={testid}>
      {((value || []).length > 0 || tbdOn) && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {tbdOn && (
            <span className="inline-flex items-center gap-1.5 text-xs font-bold px-2 py-1 rounded-md border border-dashed border-amber-500 bg-amber-50 text-amber-800" data-testid={`${testid}-tbd-chip`}>
              <HelpCircle size={12} /> TBD ×
              <input type="number" min="1" max="20" value={tbd.count} onChange={(e) => tbd.onChange(Math.max(1, Math.min(20, parseInt(e.target.value, 10) || 1)))}
                className="w-11 h-6 px-1 text-center text-xs font-bold border border-amber-300 rounded bg-white text-amber-900" data-testid={`${testid}-tbd-count`} />
              guys — pick later
              <button type="button" onClick={() => tbd.onChange(0)} className="hover:text-red-600" title="Remove TBD" data-testid={`${testid}-tbd-remove`}><X size={13} /></button>
            </span>
          )}
          {value.map((v) => {
            const vac = vacations?.[v.id];
            const hasCustom = !!(v.start_date || v.end_date);
            return (
              <span key={v.id} title={vac ? `${vac.label}` : undefined}
                className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-md border ${vac ? 'bg-orange-50 text-orange-800 border-orange-300' : 'bg-[#eef3f9] text-[#1e3a5f] border-[#d6e2f0]'}`}>
                {vac && <AlertTriangle size={11} className="text-orange-500" />}
                {v.name}
                {hasCustom && (
                  <span className="text-[10px] text-blue-700 font-bold bg-blue-100 border border-blue-200 rounded px-1" data-testid={`dr-crew-dates-${v.id}`}>
                    {fmtShort(v.start_date || jobStart)}–{fmtShort(v.end_date || jobEnd)}
                  </span>
                )}
                {vac && <span className="text-[10px] text-orange-600 font-semibold">· {vac.label}</span>}
                {(jobStart || jobEnd) && (
                  <button type="button" onClick={() => setDateEditId(dateEditId === v.id ? null : v.id)}
                    className={`hover:text-blue-700 ${hasCustom ? 'text-blue-600' : 'text-slate-500'}`}
                    title="Set this person's own dates (partial stint)" data-testid={`dr-crew-dates-btn-${v.id}`}>
                    <Calendar size={12} />
                  </button>
                )}
                <button type="button" onClick={() => remove(v.id)} className="hover:text-red-600" data-testid={`dr-crew-remove-${v.id}`}>
                  <X size={13} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      {editingMember && (
        <div className="mb-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="dr-member-dates-editor">
          <p className="text-xs font-semibold text-blue-900 mb-2">
            {editingMember.name} — their own dates on this job (defaults to the full job{jobStart ? `: ${fmtShort(jobStart)}–${fmtShort(jobEnd || jobStart)}` : ''})
          </p>
          <div className="flex items-center gap-2 flex-wrap">
            <input type="date" value={editingMember.start_date || jobStart || ''} min={jobStart || undefined} max={jobEnd || undefined}
              onChange={(e) => setMemberDate(editingMember.id, 'start_date', e.target.value)}
              className="h-9 px-2 border border-gray-300 rounded-lg text-sm" data-testid="dr-member-start-date" />
            <span className="text-xs text-gray-500">to</span>
            <input type="date" value={editingMember.end_date || jobEnd || jobStart || ''} min={jobStart || undefined} max={jobEnd || undefined}
              onChange={(e) => setMemberDate(editingMember.id, 'end_date', e.target.value)}
              className="h-9 px-2 border border-gray-300 rounded-lg text-sm" data-testid="dr-member-end-date" />
            <button type="button" onClick={() => clearMemberDates(editingMember.id)}
              className="text-xs text-gray-500 hover:text-gray-800 underline" data-testid="dr-member-dates-reset">Full job</button>
            <button type="button" onClick={() => setDateEditId(null)}
              className="text-xs font-bold text-blue-700 hover:text-blue-900" data-testid="dr-member-dates-done">Done</button>
          </div>
          <p className="text-[11px] text-blue-700/70 mt-1.5">The schedule shows the headcount change on those dates, and their dispatch text shows their dates only.</p>
        </div>
      )}
      {(() => {
        const onJob = (options || []).filter((o) => sameJob?.[o.id] && !selectedIds.has(o.id) && !busy?.[o.id]);
        if (!onJob.length) return null;
        const fromSheet = onJob.some((o) => sameJob[o.id].source === 'sheet');
        return (
          <div className="mb-2 px-3 py-2 bg-sky-50 border border-sky-200 rounded-lg text-xs text-sky-900 flex items-start justify-between gap-2 flex-wrap" data-testid={`${testid}-same-job`}>
            <span>
              <b>Already on this job{fromSheet ? ' on the dispatch sheet' : ''}:</b>{' '}
              {onJob.map((o, i) => <span key={o.id}>{i > 0 && ', '}{o.name} <span className="text-sky-700/70">({fmtRange(sameJob[o.id].start, sameJob[o.id].end)})</span></span>)}
            </span>
            <button type="button" onClick={() => onChange([...(value || []), ...onJob.map((o) => ({ id: o.id, name: o.name }))])}
              className="shrink-0 h-6 px-2 rounded-md bg-sky-700 hover:bg-sky-800 text-white font-bold" data-testid={`${testid}-same-job-add-all`}>
              Add {onJob.length === 1 ? 'him' : `all ${onJob.length}`}
            </button>
          </div>
        );
      })()}
      <div className="relative">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setTimeout(() => setFocused(false), 150)}
          placeholder={tbd ? 'Search crew to add… or pick TBD' : 'Search crew to add…'}
          className={inputCls}
          data-testid={`${testid}-search`}
        />
        {((q && filtered.length > 0) || (focused && showTbdOpt)) && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
            {showTbdOpt && (
              <button type="button" onClick={() => { tbd.onChange(tbd.defaultCount || 1); setQ(''); }}
                className="w-full text-left px-3 py-2 text-sm bg-amber-50 hover:bg-amber-100 border-b border-amber-200 flex items-center justify-between" data-testid={`${testid}-opt-tbd`}>
                <span className="font-bold text-amber-800 inline-flex items-center gap-1.5"><HelpCircle size={13} /> TBD — to be determined</span>
                <span className="text-[11px] text-amber-700">approve now, pick the names later</span>
              </button>
            )}
            {q && filtered.slice(0, 12).map((o) => {
              const b = busy?.[o.id];
              if (b) {
                return (
                  <div
                    key={o.id}
                    className="w-full text-left px-3 py-2 text-sm flex items-center justify-between opacity-60 cursor-not-allowed bg-gray-50"
                    title={`Already scheduled on ${busyLabel(b)}`}
                    data-testid={`dr-crew-opt-${o.id}`}
                  >
                    <span className="text-gray-500 line-through">{o.name}</span>
                    <span className="text-[11px] text-amber-600 font-medium ml-2 shrink-0">{busyLabel(b)}</span>
                  </div>
                );
              }
              return (
                <button
                  type="button"
                  key={o.id}
                  onClick={() => add(o)}
                  className="w-full text-left px-3 py-2 text-sm hover:bg-[#eef3f9] flex items-center justify-between"
                  data-testid={`dr-crew-opt-${o.id}`}
                >
                  <span className="text-gray-800">{o.name}</span>
                  {vacations?.[o.id] ? (
                    <span className="text-[11px] text-orange-600 font-semibold ml-2 shrink-0 inline-flex items-center gap-1">
                      <AlertTriangle size={11} /> {vacations[o.id].label}
                    </span>
                  ) : sameJob?.[o.id] ? (
                    <span className="text-[11px] text-sky-700 font-semibold ml-2 shrink-0" data-testid={`dr-crew-same-job-${o.id}`}>{sameJobLabel(sameJob[o.id])}</span>
                  ) : (
                    <span className="text-xs text-slate-500 capitalize">{(o.role || '').replace(/_/g, ' ')}</span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const DateField = ({ label, value, onChange, min, testid }) => {
  const [open, setOpen] = useState(false);
  const selected = value ? parseISO(value) : undefined;
  const minDate = min ? parseISO(min) : undefined;
  return (
    <Field label={label}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button type="button" className={`${inputCls} text-left flex items-center justify-between`} data-testid={testid}>
            <span className={value ? 'text-gray-900' : 'text-slate-500'}>{value ? format(selected, 'EEE, MMM d, yyyy') : 'Select date'}</span>
            <Calendar size={15} className="text-slate-500 shrink-0 ml-2" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <CalendarComp
            mode="single"
            selected={selected}
            defaultMonth={selected || minDate}
            disabled={minDate ? { before: minDate } : undefined}
            onSelect={(d) => { if (d) { onChange(format(d, 'yyyy-MM-dd')); setOpen(false); } }}
          />
        </PopoverContent>
      </Popover>
    </Field>
  );
};

// Multi-row equipment picker: "+" adds another row; units chosen in a sibling row OR already
// scheduled on the selected dates (busyIds) are greyed out / disabled.
const EquipMultiRow = ({ label, options, value, onChange, busy, sameJob, testid }) => {
  const [rows, setRows] = useState(() => {
    const ids = (value || []).map((v) => v.id).filter(Boolean);
    return ids.length ? ids : [''];
  });
  const commit = (next) => {
    setRows(next);
    const picked = next.filter(Boolean).map((id) => {
      const o = (options || []).find((x) => x.id === id);
      return o ? { id: o.id, name: o.name } : null;
    }).filter(Boolean);
    onChange(picked);
  };
  const setRow = (i, id) => { const n = [...rows]; n[i] = id; commit(n); };
  const addRow = () => setRows((r) => [...r, '']);
  const removeRow = (i) => { const n = rows.filter((_, idx) => idx !== i); commit(n.length ? n : ['']); };
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-xs font-medium text-gray-600">{label}</label>
        <button type="button" onClick={addRow} title={`Add another ${label.toLowerCase()}`}
          className="inline-flex items-center justify-center h-6 w-6 rounded-md bg-gray-100 hover:bg-[#1e3a5f] hover:text-white text-gray-500 transition-colors" data-testid={`${testid}-add`}>
          <Plus size={14} />
        </button>
      </div>
      <div className="space-y-2">
        {rows.map((rid, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <select value={rid} onChange={(e) => setRow(i, e.target.value)} className={inputCls} data-testid={`${testid}-${i}`}>
              <option value="">— None —</option>
              {(options || []).map((o) => {
                const b = busy?.[o.id];
                const chosenElsewhere = rows.some((r, idx) => idx !== i && r === o.id);
                const disabled = (b || chosenElsewhere) && rid !== o.id;
                const suffix = b ? ` — ${busyLabel(b)}` : chosenElsewhere ? ' — selected' : sameJob?.[o.id] ? ` — ${sameJobLabel(sameJob[o.id])}` : '';
                return (
                  <option key={o.id} value={o.id} disabled={disabled}>
                    {o.name}{suffix}
                  </option>
                );
              })}
            </select>
            {rows.length > 1 && (
              <button type="button" onClick={() => removeRow(i)} className="text-slate-700 hover:text-red-500 shrink-0" title="Remove" data-testid={`${testid}-remove-${i}`}>
                <X size={16} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Request / Edit form ─────────────────────────────────────────────
const RequestForm = ({ resources, initial, onCancel, onSaved }) => {
  const editing = !!initial?.id;
  const approvedEdit = editing && initial?.status === 'approved';
  const [f, setF] = useState({
    customer_name: initial?.customer_name || '',
    po_number: initial?.po_number || '',
    job_site: initial?.job_site || '',
    work_order_number: initial?.work_order_number || '',
    start_date: initial?.start_date || todayISO(),
    end_date: initial?.end_date || initial?.start_date || todayISO(),
    start_time: initial?.start_time || '07:00',
    end_time: initial?.end_time || '15:30',
    contact: initial?.contact || '',
    contact_phone: initial?.contact_phone || '',
    tool_list: initial?.tool_list || '',
    tools: initial?.tools || [],
    description: initial?.description || '',
    requested_crew: initial?.requested_crew || [],
    unnamed_crew_count: initial?.unnamed_crew_count || 0,
    trucks: initial?.trucks || (initial?.truck ? [initial.truck] : []),
    trailers: initial?.trailers || (initial?.trailer ? [initial.trailer] : []),
    welders: initial?.welders || (initial?.welder ? [initial.welder] : []),
    forklifts: initial?.forklifts || (initial?.forklift ? [initial.forklift] : []),
    no_weekends: initial?.no_weekends || false,
    skill_requirements: initial?.skill_requirements || [],
  });
  const [saving, setSaving] = useState(false);
  const [busyMap, setBusyMap] = useState({});
  const [sameJobMap, setSameJobMap] = useState({});
  const [vacMap, setVacMap] = useState({});
  const [pmSuggest, setPmSuggest] = useState(null); // WO's PM — suggested, never auto-added
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  // Auto-fill customer + last address from the Work Order # (debounced)
  useEffect(() => {
    const wo = (f.work_order_number || '').trim();
    if (!wo) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/dispatch/wo-lookup`, { params: { wo }, ...authHeader() });
        const d = res.data || {};
        if (d.matched && d.customer_name) set('customer_name', d.customer_name);
        if (d.job_site) set('job_site', d.job_site);
        setF((p) => ({ ...p, contact: p.contact || d.contact || '', contact_phone: p.contact_phone || d.contact_phone || '' }));
        // This WO belongs to a PM — SUGGEST adding them (never auto-add; that put PMs on jobs unnoticed)
        if (d.project_manager && d.project_manager.id) {
          setPmSuggest(d.project_manager);
        } else {
          setPmSuggest(null);
        }
      } catch (e) { /* silent */ }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [f.work_order_number]);

  // Fetch which equipment is already scheduled on the chosen dates (debounced)
  useEffect(() => {
    if (!f.start_date) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/dispatch/equipment-availability`, {
          params: { start: f.start_date, end: f.end_date || f.start_date, exclude_request: initial?.id || '', wo: (f.work_order_number || '').trim() },
          ...authHeader(),
        });
        setBusyMap(res.data?.busy || {});
        setSameJobMap(res.data?.same_job || {});
        setVacMap(res.data?.vacations || {});
      } catch (e) { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [f.start_date, f.end_date, f.work_order_number]);

  const submit = async () => {
    if (!f.start_date) { toast.error('A start date is required'); return; }
    if (!f.work_order_number?.trim()) { toast.error('A Work Order # is required so the job appears on the schedule'); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = approvedEdit ? Object.fromEntries(Object.entries(f).filter(([k]) => !['requested_crew', 'unnamed_crew_count'].includes(k))) : f;
        const res = await axios.put(`${API}/dispatch/requests/${initial.id}`, payload, authHeader());
        const cn = res.data.change_notice;
        if (cn?.summary) {
          toast.success(`${res.data.request_number} updated — ${cn.summary}. Crew notified in Job Chat.${cn.crew_texted_before ? ' They were already texted the old details — use Dispatch / Re-post to text the update.' : ''}`, { duration: 9000 });
        } else {
          toast.success(`Request ${res.data.request_number} updated`);
        }
      } else {
        const res = await axios.post(`${API}/dispatch/requests`, f, authHeader());
        toast.success(`Dispatch request ${res.data.request_number} sent to dispatch`);
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save request');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="dr-form-page">
      <button onClick={onCancel} className="inline-flex items-center gap-1.5 text-sm text-blue-800 hover:text-slate-900 mb-4" data-testid="dr-form-back">
        <ArrowLeft size={16} /> Back to requests
      </button>
      <h1 className="text-2xl font-bold text-slate-900 mb-1">{approvedEdit ? `Edit Details — ${initial.request_number}` : editing ? 'Edit Dispatch Request' : 'New Dispatch Request'}</h1>
      <p className="text-sm text-blue-800 mb-6">{approvedEdit ? 'This job is already approved and on the schedule. Saving updates the schedule bars and posts the change in the Job Chat; crew changes are made with Modify Crew.' : 'Request crew and equipment for a job — dispatch reviews and schedules it.'}</p>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-6 shadow-sm">
        {/* Job details */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Customer">
            <input className={inputCls} value={f.customer_name} onChange={(e) => set('customer_name', e.target.value)} data-testid="dr-customer" />
          </Field>
          <Field label="Work Order # *">
            <input className={inputCls} value={f.work_order_number} onChange={(e) => set('work_order_number', e.target.value)} placeholder="Required — must match a project (e.g. 26-3057)" data-testid="dr-wo" />
          </Field>
          <Field label="Job Site / Address">
            <input className={inputCls} value={f.job_site} onChange={(e) => set('job_site', e.target.value)} data-testid="dr-jobsite" />
          </Field>
          <Field label="Site Contact">
            <input className={inputCls} value={f.contact} onChange={(e) => set('contact', e.target.value)} placeholder="Name" data-testid="dr-contact" />
          </Field>
          <Field label="Site Contact Phone">
            <input type="tel" className={inputCls} value={f.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} placeholder="e.g. 519-555-0123" data-testid="dr-contact-phone" />
          </Field>
          <Field label="Customer PO #">
            <input className={inputCls} value={f.po_number} onChange={(e) => set('po_number', e.target.value)} placeholder="Optional — shows on the card and the schedule bar" data-testid="dr-po" />
          </Field>
        </div>

        {/* Schedule */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <DateField label="Start Date" value={f.start_date} min={editing ? undefined : todayISO()} testid="dr-start-date"
            onChange={(v) => { set('start_date', v); if (!f.end_date || f.end_date < v) set('end_date', v); }} />
          <DateField label="End Date" value={f.end_date} min={f.start_date} testid="dr-end-date"
            onChange={(v) => set('end_date', v)} />
          <Field label="Start Time">
            <input type="time" className={inputCls} value={f.start_time} onChange={(e) => set('start_time', e.target.value)} data-testid="dr-start-time" />
          </Field>
          <Field label="End Time">
            <input type="time" className={inputCls} value={f.end_time} onChange={(e) => set('end_time', e.target.value)} data-testid="dr-end-time" />
          </Field>
        </div>

        {/* Weekend exclusion */}
        <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="dr-no-weekends-label">
          <input type="checkbox" checked={f.no_weekends} onChange={(e) => set('no_weekends', e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-[#1e3a5f] focus:ring-[#1e3a5f]" data-testid="dr-no-weekends" />
          <span className="text-sm text-gray-700">
            <span className="font-medium">Exclude weekends</span> (Sat &amp; Sun) — the job shows as separate weekday bubbles on the schedule
          </span>
        </label>

        {/* Crew */}
        {approvedEdit ? (
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-4 py-3 text-sm text-slate-700" data-testid="dr-approved-crew-note">
            <b>Crew:</b> {(initial.assigned_crew || []).map((c) => c.name).join(', ') || 'none'} — change who's going with <b>Modify Crew</b> on the request card.
          </div>
        ) : (<>
        <Field label="Requested Crew">
          {pmSuggest && !(f.requested_crew || []).some((c) => c.id === pmSuggest.id) && (
            <div className="flex items-center justify-between gap-2 mb-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2" data-testid="dr-pm-suggest">
              <span className="text-xs text-blue-800">
                <b>{pmSuggest.name}</b> is the PM on this WO — want them on the crew?
              </span>
              <span className="flex items-center gap-2">
                <button type="button" data-testid="dr-pm-suggest-add"
                  onClick={() => { set('requested_crew', [...(f.requested_crew || []), { id: pmSuggest.id, name: pmSuggest.name }]); setPmSuggest(null); }}
                  className="text-xs font-semibold text-blue-700 hover:text-blue-900 bg-white border border-blue-300 rounded px-2 py-1">+ Add</button>
                <button type="button" data-testid="dr-pm-suggest-dismiss" onClick={() => setPmSuggest(null)}
                  className="text-xs text-blue-700 hover:text-blue-700">✕</button>
              </span>
            </div>
          )}
          <CrewPicker options={resources.crew} value={f.requested_crew} onChange={(v) => set('requested_crew', v)} busy={busyMap} sameJob={sameJobMap} vacations={vacMap} testid="dr-crew-picker" jobStart={f.start_date} jobEnd={f.end_date || f.start_date} />
        </Field>

        {/* Skills needed → MASON picks the best available combination */}
        <Field label="Skills Needed — MASON Crew Suggestion">
          <SkillsNeeded value={f.skill_requirements} onChange={(v) => set('skill_requirements', v)} scope={f.description} startDate={f.start_date} endDate={f.end_date}
            headcount={(f.requested_crew?.length || 0) + (f.unnamed_crew_count || 0) || undefined} wo={(f.work_order_number || '').trim()} customer={f.customer_name} currentCrew={f.requested_crew} excludeRequest={initial?.request_number}
            onUseCrew={(picks) => setF((p) => ({ ...p, requested_crew: [...(p.requested_crew || []), ...picks.filter((x) => !(p.requested_crew || []).some((c) => c.id === x.id))], unnamed_crew_count: Math.max(0, (p.unnamed_crew_count || 0) - picks.length) }))} />
        </Field>

        {/* Unnamed headcount — dispatch fills in the names */}
        <Field label="Extra Crew — Dispatch Picks Who">
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={() => set('unnamed_crew_count', Math.max(0, (f.unnamed_crew_count || 0) - 1))}
              className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 font-bold hover:bg-gray-100" data-testid="dr-unnamed-minus">−</button>
            <input type="number" min="0" max="20" className="w-20 h-9 px-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30" value={f.unnamed_crew_count || 0}
              onChange={(e) => set('unnamed_crew_count', Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0)))} data-testid="dr-unnamed-count" />
            <button type="button" onClick={() => set('unnamed_crew_count', Math.min(20, (f.unnamed_crew_count || 0) + 1))}
              className="w-9 h-9 rounded-lg border border-gray-300 text-gray-600 font-bold hover:bg-gray-100" data-testid="dr-unnamed-plus">+</button>
            <span className="text-xs text-gray-500">Don't know who's available? Just say how many extra guys you need — dispatch fills in the names before approving.</span>
          </div>
        </Field>
        </>)}

        {/* Equipment */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <EquipMultiRow label="Truck" options={resources.trucks} value={f.trucks} onChange={(v) => set('trucks', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-truck" />
          <EquipMultiRow label="Trailer" options={resources.trailers} value={f.trailers} onChange={(v) => set('trailers', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-trailer" />
          <EquipMultiRow label="Welder" options={resources.welders} value={f.welders} onChange={(v) => set('welders', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-welder" />
          <EquipMultiRow label="Forklift" options={resources.forklifts} value={f.forklifts} onChange={(v) => set('forklifts', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-forklift" />
        </div>

        {/* Tools needed — from the master 365ISI tool list */}
        <Field label="Tools Needed">
          <DispatchToolPicker value={f.tools} onChange={(v) => set('tools', v)} />
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Other Tools / Materials (free text)">
            <textarea className={`${inputCls} h-24 resize-none`} value={f.tool_list} onChange={(e) => set('tool_list', e.target.value)} placeholder="Anything not in the tool list above" data-testid="dr-tools" />
          </Field>
          <Field label="Description / Scope">
            <textarea className={`${inputCls} h-24 resize-none`} value={f.description} onChange={(e) => set('description', e.target.value)} placeholder="What's the work?" data-testid="dr-description" />
          </Field>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onCancel} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100" data-testid="dr-form-cancel">Cancel</button>
          <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#16304f] text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-60" data-testid="dr-form-submit">
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            {editing ? 'Save Changes' : 'Send to Dispatch'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Approve modal ───────────────────────────────────────────────────
const ApproveModal = ({ req, resources, onClose, onApproved }) => {
  const [f, setF] = useState({
    work_order_number: req.work_order_number || '',
    start_date: req.start_date || todayISO(),
    end_date: req.end_date || req.start_date || todayISO(),
    start_time: req.start_time || '07:00',
    end_time: req.end_time || '15:30',
    assigned_crew: req.requested_crew || [],
    tbd_count: 0,
    trucks: req.trucks || (req.truck ? [req.truck] : []),
    trailers: req.trailers || (req.trailer ? [req.trailer] : []),
    welders: req.welders || (req.welder ? [req.welder] : []),
    forklifts: req.forklifts || (req.forklift ? [req.forklift] : []),
  });
  const [busy, setBusy] = useState(false);
  const [busyMap, setBusyMap] = useState({});
  const [sameJobMap, setSameJobMap] = useState({});
  const [vacMap, setVacMap] = useState({});
  const [ackConflicts, setAckConflicts] = useState(false);
  const [woOverlaps, setWoOverlaps] = useState([]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const requestedTotal = (req.requested_crew?.length || 0) + (req.unnamed_crew_count || 0);
  const tbdDefault = Math.max(1, requestedTotal - (f.assigned_crew || []).length);

  useEffect(() => {
    if (!f.start_date) return;
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/dispatch/equipment-availability`, {
          params: { start: f.start_date, end: f.end_date || f.start_date, exclude_request: req.id, wo: (f.work_order_number || '').trim() },
          ...authHeader(),
        });
        setBusyMap(res.data?.busy || {});
        setSameJobMap(res.data?.same_job || {});
        setVacMap(res.data?.vacations || {});
        setAckConflicts(false);
      } catch (e) { /* silent */ }
      try {
        if ((f.work_order_number || '').trim()) {
          const ov = await axios.get(`${API}/dispatch/wo-overlap`, {
            params: { wo: f.work_order_number.trim(), start: f.start_date, end: f.end_date || f.start_date, exclude_request: req.id },
            ...authHeader(),
          });
          setWoOverlaps(ov.data?.overlaps || []);
        } else {
          setWoOverlaps([]);
        }
      } catch (e) { /* silent */ }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [f.start_date, f.end_date, f.work_order_number]);

  const approve = async () => {
    if (!f.work_order_number?.trim()) { toast.error('A work order # is required to add this to the schedule'); return; }
    const conflicts = (f.assigned_crew || []).filter((c) => vacMap[c.id]);
    if (conflicts.length > 0 && !ackConflicts) {
      setAckConflicts(true);
      toast.warning(`Booked off: ${conflicts.map((c) => `${c.name} — ${vacMap[c.id].label}`).join(' · ')}. Click "Approve anyway" if you're sure.`, { duration: 10000 });
      return;
    }
    setBusy(true);
    try {
      if ((f.work_order_number || '').trim() !== (req.work_order_number || '').trim()) {
        await axios.put(`${API}/dispatch/requests/${req.id}`, { work_order_number: f.work_order_number.trim() }, authHeader());
      }
      const res = await axios.post(`${API}/dispatch/requests/${req.id}/approve`, {
        assigned_crew: f.assigned_crew,
        crew_tbd: (f.tbd_count || 0) > 0, tbd_count: f.tbd_count || 0,
        start_date: f.start_date, end_date: f.end_date,
        start_time: f.start_time, end_time: f.end_time,
        trucks: f.trucks, trailers: f.trailers, welders: f.welders, forklifts: f.forklifts,
      }, authHeader());
      if (res.data?.project_matched === false) {
        toast.warning(`Approved — WO# didn't match an existing project, added to the schedule as-is`);
      } else if ((f.tbd_count || 0) > 0) {
        toast.success(`Approved with ${f.tbd_count} crew TBD — on the schedule as "${f.tbd_count}?"; the tool list and Job Kit are open. Pick the names with Modify Crew.`, { duration: 8000 });
      } else {
        toast.success(`Approved — added to the schedule`);
      }
      onApproved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="dr-approve-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Review & Approve</h2>
            <p className="text-xs text-gray-500">{req.request_number} · from {req.created_by_name}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700" data-testid="dr-approve-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Work Order #">
              <input className={inputCls} value={f.work_order_number} onChange={(e) => set('work_order_number', e.target.value)} placeholder="Required to schedule" data-testid="dr-approve-wo" />
            </Field>
            <div className="text-sm text-gray-500 flex items-end pb-2">
              <span><MapPin size={13} className="inline mr-1" />{req.job_site || '—'} · {req.customer_name || '—'}</span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <DateField label="Start Date" value={f.start_date} testid="dr-approve-start-date"
              onChange={(v) => { set('start_date', v); if (!f.end_date || f.end_date < v) set('end_date', v); }} />
            <DateField label="End Date" value={f.end_date} min={f.start_date} testid="dr-approve-end-date"
              onChange={(v) => set('end_date', v)} />
            <Field label="Start Time"><input type="time" className={inputCls} value={f.start_time} onChange={(e) => set('start_time', e.target.value)} /></Field>
            <Field label="End Time"><input type="time" className={inputCls} value={f.end_time} onChange={(e) => set('end_time', e.target.value)} /></Field>
          </div>
          {woOverlaps.length > 0 && (() => {
            const allSheet = woOverlaps.every((o) => o.source === 'sheet');
            return (
            <div className={`px-3 py-2 rounded-lg text-xs border ${allSheet ? 'bg-sky-50 border-sky-200 text-sky-900' : 'bg-amber-50 border-amber-300 text-amber-800'}`} data-testid="dr-approve-wo-overlap-warning">
              <b>{allSheet ? `WO ${f.work_order_number} is on the dispatch sheet:` : `⚠ WO ${f.work_order_number} is already booked:`}</b>{' '}
              {woOverlaps.map((o, i) => (
                <span key={i}>
                  {i > 0 && ' · '}
                  {o.start_date}{o.end_date && o.end_date !== o.start_date ? ` – ${o.end_date}` : ''}
                  {o.crew?.length ? ` with ${o.crew.join(', ')}` : ''}
                  {!allSheet && o.source === 'sheet' ? ' (sheet)' : ''}
                </span>
              ))}
              {allSheet
                ? <span className="block mt-1">Approving puts <b>this request's crew</b> on those days — the sheet bar for them is replaced, not doubled.</span>
                : <span className="block mt-1">Approving will add a <b>second crew booking</b> on this work order — make sure that's intended.</span>}
            </div>
            );
          })()}
          {(req.unnamed_crew_count || 0) > 0 && (() => {
            const total = (req.requested_crew?.length || 0) + (req.unnamed_crew_count || 0);
            const missing = Math.max(0, total - (f.assigned_crew || []).length - (f.tbd_count || 0));
            return (
              <div className={`px-3 py-2 rounded-lg text-xs border ${missing > 0 ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-green-50 border-green-200 text-green-700'}`} data-testid="dr-approve-unnamed-banner">
                <b>{req.created_by_name || 'The PM'} asked for {total} crew total</b> — {req.requested_crew?.length || 0} named + {req.unnamed_crew_count} for dispatch to pick.
                {missing > 0
                  ? <span className="block mt-0.5">{missing} slot{missing !== 1 ? 's' : ''} still to fill — add them below, or approve now and add crew later via Modify Crew.</span>
                  : <span className="block mt-0.5">All slots filled ✓</span>}
              </div>
            );
          })()}
          <Field label="Assigned Crew">
            <CrewPicker options={resources.crew} value={f.assigned_crew} onChange={(v) => { set('assigned_crew', v); setAckConflicts(false); }} busy={busyMap} sameJob={sameJobMap} vacations={vacMap} testid="dr-approve-crew" jobStart={f.start_date} jobEnd={f.end_date || f.start_date}
              tbd={{ count: f.tbd_count || 0, defaultCount: tbdDefault, onChange: (n) => set('tbd_count', n) }} />
            {(f.tbd_count || 0) > 0 && (
              <p className="mt-1.5 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" data-testid="dr-approve-tbd-note">
                Approving with <b>{f.tbd_count} TBD</b>: the job goes on the schedule as an amber <b>{f.tbd_count}?</b> bar, the tool list / Job Kit open right away, and nobody is texted until you name the crew with <b>Modify Crew</b>.
              </p>
            )}
            {(f.assigned_crew || []).length === 0 && !(f.tbd_count > 0) && (
              <p className="mt-1.5 text-[11px] text-slate-500" data-testid="dr-approve-crew-hint">Don't know who's going yet? Click the box and choose <b>TBD</b>.</p>
            )}
          </Field>
          {(f.assigned_crew || []).some((c) => vacMap[c.id]) && (
            <div className="px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg text-xs text-orange-800" data-testid="dr-approve-conflict-warning">
              <b>Booked off:</b> {(f.assigned_crew || []).filter((c) => vacMap[c.id]).map((c) => `${c.name} — ${vacMap[c.id].label}`).join(' · ')}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <EquipMultiRow label="Truck" options={resources.trucks} value={f.trucks} onChange={(v) => set('trucks', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-approve-truck" />
            <EquipMultiRow label="Trailer" options={resources.trailers} value={f.trailers} onChange={(v) => set('trailers', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-approve-trailer" />
            <EquipMultiRow label="Welder" options={resources.welders} value={f.welders} onChange={(v) => set('welders', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-approve-welder" />
            <EquipMultiRow label="Forklift" options={resources.forklifts} value={f.forklifts} onChange={(v) => set('forklifts', v)} busy={busyMap} sameJob={sameJobMap} testid="dr-approve-forklift" />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={approve} disabled={busy} className={`inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60 ${ackConflicts ? 'bg-orange-600 hover:bg-orange-700' : 'bg-green-600 hover:bg-green-700'}`} data-testid="dr-approve-confirm">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} {ackConflicts ? 'Approve anyway' : 'Approve & Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Modify crew on an approved dispatch (e.g. someone declined) ─────
// Exported: also used from the Schedule work-period modal.
export const ModifyCrewModal = ({ req, resources, onClose, onSaved }) => {
  const [crew, setCrew] = useState(req.assigned_crew || []);
  const [busyMap, setBusyMap] = useState({});
  const [sameJobMap, setSameJobMap] = useState({});
  const [vacMap, setVacMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [ackConflicts, setAckConflicts] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/dispatch/equipment-availability`, {
          params: { start: req.start_date, end: req.end_date || req.start_date, exclude_request: req.id, wo: (req.work_order_number || '').trim() },
          ...authHeader(),
        });
        setBusyMap(res.data?.busy || {});
        setSameJobMap(res.data?.same_job || {});
        setVacMap(res.data?.vacations || {});
      } catch (e) { /* silent */ }
    })();
    // eslint-disable-next-line
  }, []);

  const save = async () => {
    if (crew.length === 0) { toast.error('At least one crew member is required'); return; }
    const conflicts = crew.filter((c) => vacMap[c.id]);
    if (conflicts.length > 0 && !ackConflicts) {
      setAckConflicts(true);
      toast.warning(`Booked off: ${conflicts.map((c) => `${c.name} — ${vacMap[c.id].label}`).join(' · ')}. Click "Save anyway" if you're sure.`, { duration: 10000 });
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/dispatch/requests/${req.id}/modify-crew`, { assigned_crew: crew }, authHeader());
      const { added = [], removed = [], pm_fyi_notified = [] } = res.data || {};
      const parts = [];
      if (added.length) parts.push(`added ${added.map((c) => c.name).join(', ')}`);
      if (removed.length) parts.push(`removed ${removed.map((c) => c.name).join(', ')}`);
      toast.success(`Crew updated${parts.length ? ` — ${parts.join(' · ')}` : ''}`);
      if (pm_fyi_notified.length) toast.info(`PM notified of the change: ${pm_fyi_notified.join(', ')}`);
      onSaved(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update crew');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" data-testid="dr-modify-crew-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Modify Crew</h2>
            <p className="text-xs text-gray-500">{req.request_number} · {fmtRange(req.start_date, req.end_date)} · {req.customer_name || req.job_site || ''}</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700" data-testid="dr-modify-crew-close"><X size={20} /></button>
        </div>
        <div className="p-6 space-y-4">
          <Field label="Assigned Crew">
            <CrewPicker options={resources.crew} value={crew} onChange={(v) => { setCrew(v); setAckConflicts(false); }} busy={busyMap} sameJob={sameJobMap} vacations={vacMap} testid="dr-modify-crew-picker" jobStart={req.start_date} jobEnd={req.end_date || req.start_date} />
          </Field>
          {crew.some((c) => vacMap[c.id]) && (
            <div className="px-3 py-2 bg-orange-50 border border-orange-300 rounded-lg text-xs text-orange-800" data-testid="dr-modify-conflict-warning">
              <b>Booked off:</b> {crew.filter((c) => vacMap[c.id]).map((c) => `${c.name} — ${vacMap[c.id].label}`).join(' · ')}
            </div>
          )}
          {(req.unnamed_crew_count || 0) > 0 && (() => {
            const total = (req.requested_crew?.length || 0) + (req.unnamed_crew_count || 0) - (req.cleared_slots || 0);
            const left = Math.max(0, total - crew.length);
            return (
              <p className="text-xs text-amber-700" data-testid="dr-modify-unnamed-hint">
                PM asked for {total} crew total{(req.cleared_slots || 0) > 0 ? ` (${req.cleared_slots} slot${req.cleared_slots !== 1 ? 's' : ''} cleared)` : ''}{left > 0 ? ` — ${left} slot${left !== 1 ? 's' : ''} still to fill.` : ' — all slots filled ✓'}
              </p>
            );
          })()}
          <p className="text-xs text-slate-500">The schedule updates immediately. If the dispatch text already went out, you'll be offered to text just the new member(s) — nobody gets a duplicate.</p>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={save} disabled={busy} className={`inline-flex items-center gap-2 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60 ${ackConflicts ? 'bg-orange-600 hover:bg-orange-700' : 'bg-[#1e3a5f] hover:bg-[#16304f]'}`} data-testid="dr-modify-crew-save">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle2 size={16} />} {ackConflicts ? 'Save anyway' : 'Save Crew'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Decline modal ───────────────────────────────────────────────────
const DeclineModal = ({ req, onClose, onDeclined }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/dispatch/requests/${req.id}/decline`, { reason }, authHeader());
      toast.success(`${req.request_number} declined`);
      onDeclined();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to decline');
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="dr-decline-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-bold text-gray-900">Decline {req.request_number}</h2>
          <button onClick={onClose} className="text-slate-500 hover:text-gray-700"><X size={20} /></button>
        </div>
        <div className="p-6">
          <Field label="Reason (optional)">
            <textarea className={`${inputCls} h-28 resize-none`} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Let the requester know why…" data-testid="dr-decline-reason" />
          </Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose} className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100">Cancel</button>
          <button onClick={submit} disabled={busy} className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-lg text-sm font-semibold disabled:opacity-60" data-testid="dr-decline-confirm">
            {busy ? <Loader2 className="animate-spin" size={16} /> : <XCircle size={16} />} Decline
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Request card ────────────────────────────────────────────────────
const RequestCard = ({ req, canApprove, isOwner, userId, onApprove, onDecline, onEdit, onDelete, onCancelSms, onPrintSheet, onLogLoaded, onModifyCrew, onSendText, onCancelJob, onRebook, onClearSlot, onAmend, onAmendmentsChanged, onRateJob }) => {
  const equip = [
    ...(req.trucks || (req.truck ? [req.truck] : [])),
    ...(req.trailers || (req.trailer ? [req.trailer] : [])),
    ...(req.welders || (req.welder ? [req.welder] : [])),
    ...(req.forklifts || (req.forklift ? [req.forklift] : [])),
  ].filter(Boolean);
  const crew = (req.status === 'approved' ? req.assigned_crew : req.requested_crew) || [];
  const totalRequested = (req.requested_crew?.length || 0) + (req.unnamed_crew_count || 0);
  const clearedSlots = req.cleared_slots || 0;
  const unfilledSlots = req.status === 'pending'
    ? (req.unnamed_crew_count || 0)
    : (req.status === 'approved' ? Math.max(0, totalRequested - crew.length - clearedSlots) : 0);
  const sched = req.scheduled_sms?.status === 'pending' ? req.scheduled_sms : null;
  const schedTime = sched?.send_at_display
    ? new Date(sched.send_at_display).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
    : null;
  return (
    <div className={`bg-white rounded-xl border p-5 shadow-sm hover:shadow-md transition-shadow ${req.customer_cancelled ? 'border-red-300 ring-2 ring-red-100' : 'border-gray-200'}`} data-testid={`dr-card-${req.id}`}>
      {req.customer_cancelled && (
        <div className="mb-3 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-800" data-testid={`dr-customer-cancelled-${req.id}`}>
          <AlertTriangle size={16} className="shrink-0 mt-0.5" />
          <div><b>⚠ Customer cancelled this request</b>{req.customer_cancelled_by ? ` (${req.customer_cancelled_by}` : ''}{req.customer_cancelled_at ? `${req.customer_cancelled_by ? ', ' : ' ('}${new Date(req.customer_cancelled_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })})` : (req.customer_cancelled_by ? ')' : '')}{req.customer_cancel_reason ? ` — “${req.customer_cancel_reason}”` : ''}. {req.status === 'approved' ? 'Crew was booked — use Cancel Job to pull them and notify everyone.' : 'Decline or delete it if the job is off.'}</div>
        </div>
      )}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-bold text-[#1e3a5f]">{req.request_number}</span>
          <StatusBadge status={req.status} />
          {req.portal_request_number && <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-sky-50 text-sky-800 border border-sky-200 rounded-full px-2 py-0.5" title="Came in through the Customer Portal and was approved by a PM" data-testid={`dr-portal-${req.id}`}><Globe size={11} /> Portal {req.portal_request_number}</span>}
        </div>
        <span className="text-xs text-slate-500">by {req.created_by_name}</span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
        <div className="text-gray-700"><span className="text-slate-500">Customer:</span> {req.customer_name || '—'}</div>
        <div className="text-gray-700"><span className="text-slate-500">WO #:</span> {req.work_order_number || <span className="text-amber-600 font-medium">missing</span>}{req.po_number && <span className="ml-3" data-testid={`dr-po-${req.id}`}><span className="text-slate-500">PO #:</span> <span className="font-mono font-semibold text-emerald-700">{req.po_number}</span></span>}</div>
        <div className="text-gray-700"><MapPin size={13} className="inline text-slate-500 mr-1" />{req.job_site || '—'}</div>
        <div className="text-gray-700"><Calendar size={13} className="inline text-slate-500 mr-1" />{fmtDate(req.start_date)}{req.end_date && req.end_date !== req.start_date ? ` – ${fmtDate(req.end_date)}` : ''} · {req.start_time}–{req.end_time}</div>
        {(req.contact || req.contact_phone) && (
          <div className="text-gray-700" data-testid={`dr-card-contact-${req.id}`}>
            <Phone size={13} className="inline text-slate-500 mr-1" />
            {req.contact || 'Site contact'}{req.contact_phone ? <> · <a href={`tel:${req.contact_phone}`} className="text-[#1e3a5f] font-medium hover:underline">{req.contact_phone}</a></> : ''}
          </div>
        )}
      </div>

      {(crew.length > 0 || equip.length > 0 || unfilledSlots > 0) && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {crew.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1 bg-[#eef3f9] text-[#1e3a5f] text-xs font-medium px-2 py-0.5 rounded"><Users size={11} />{c.name}</span>
          ))}
          {Array.from({ length: unfilledSlots }).map((_, i) => (
            <span key={`unnamed-${i}`} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 border border-dashed border-amber-400 text-xs font-medium px-2 py-0.5 rounded" data-testid={`dr-unnamed-slot-${req.id}-${i}`}>
              <Users size={11} />{req.status === 'approved' ? (req.crew_tbd ? 'TBD — pick with Modify Crew' : 'Slot to fill') : 'TBD — dispatch picks'}
              {req.status === 'approved' && canApprove && (
                <button
                  onClick={() => onClearSlot(req)}
                  title="Clear this slot — crew will be smaller than requested"
                  data-testid={`dr-clear-slot-${req.id}-${i}`}
                  className="ml-0.5 -mr-0.5 text-amber-600 hover:text-red-600 hover:bg-red-50 rounded-full p-0.5 leading-none"
                >
                  <X size={11} />
                </button>
              )}
            </span>
          ))}
          {equip.map((e) => (
            <span key={e.id} className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-medium px-2 py-0.5 rounded"><Truck size={11} />{e.name}</span>
          ))}
        </div>
      )}

      {req.description && <p className="mt-3 text-sm text-gray-600"><FileText size={13} className="inline text-slate-500 mr-1" />{req.description}</p>}

      {((Array.isArray(req.tools) && req.tools.length > 0) || (req.tool_amendments || []).length > 0 || ((canApprove || isOwner) && !['declined', 'cancelled'].includes(req.status))) && (
        <div className="mt-3" data-testid={`dr-tools-${req.id}`}>
          <div className="flex items-center gap-2 mb-1.5">
            <p className="text-xs font-semibold text-[#1e3a5f] flex items-center gap-1"><Wrench size={12} /> Tools ({(req.tools || []).length}){(req.tool_amendments || []).length > 0 && <span className="text-amber-800 font-normal">+ {(req.tool_amendments || []).length} amendment{(req.tool_amendments || []).length !== 1 ? 's' : ''}</span>}</p>
            {(canApprove || isOwner) && !['declined', 'cancelled'].includes(req.status) && (
              <button onClick={() => onAmend(req, null)} className="text-[11px] font-bold text-amber-800 hover:underline inline-flex items-center gap-1" title="Add a dated tool list under the original — for jobs that need a new list every day" data-testid={`dr-amend-tools-${req.id}`}>+ Tool list amendment</button>
            )}
          </div>
          {Array.isArray(req.tools) && req.tools.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {req.tools.map((t) => (
                <span key={t.id} className="inline-flex items-center gap-1 bg-amber-50 text-amber-800 border border-amber-200 text-xs font-medium px-2 py-0.5 rounded">
                  {t.qty > 1 && <span className="opacity-70">{t.qty}×</span>}{t.label}
                </span>
              ))}
            </div>
          )}
          <ToolAmendments req={req} canEdit={canApprove || isOwner} userId={userId} isDispatch={canApprove} onEdit={(a) => onAmend(req, a)} onChanged={(list) => onAmendmentsChanged(req.id, list)} onPrint={(a) => printToolSheet(req.id, a.id)} />
        </div>
      )}
      {req.status === 'declined' && req.decline_reason && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2">Declined: {req.decline_reason}</p>
      )}
      {req.status === 'cancelled' && (
        <p className="mt-2 text-sm text-red-600 bg-red-50 rounded-md px-3 py-2" data-testid={`dr-cancelled-note-${req.id}`}>
          Cancelled{req.cancelled_by_name ? ` by ${req.cancelled_by_name}` : ''}{req.cancel_note ? ` — ${req.cancel_note}` : ''}
          {req.rebooked_to ? <span className="block text-emerald-700 font-medium mt-0.5">Re-booked as {req.rebooked_to}</span> : ''}
        </p>
      )}
      {req.rebooked_from && req.status !== 'cancelled' && (
        <p className="mt-2 text-xs text-emerald-700" data-testid={`dr-rebooked-from-${req.id}`}>Re-booked from {req.rebooked_from}.</p>
      )}
      {req.status === 'cancelled' && Array.isArray(req.cancel_recipients) && req.cancel_recipients.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`dr-cancel-status-${req.id}`}>
          <p className="text-xs font-semibold text-red-700 mb-2 uppercase tracking-wide flex items-center gap-1.5">
            <MessageSquare size={13} /> Cancellation notice status
          </p>
          <div className="flex flex-wrap gap-1.5">
            {req.cancel_recipients.map((rec, i) => (
              <DispatchStatusChip key={rec.crew_member_id || rec.crew_member_phone || i} rec={rec} />
            ))}
          </div>
        </div>
      )}
      {req.status === 'approved' && (
        <p className="mt-2 text-xs text-green-700">Added to the schedule{req.reviewed_by_name ? ` by ${req.reviewed_by_name}` : ''}.
          {req.crew_tbd && unfilledSlots > 0 && <span className="ml-1 text-amber-700 font-semibold" data-testid={`dr-tbd-note-${req.id}`}>Crew TBD — {unfilledSlots} to pick (Modify Crew). Nobody texted yet.</span>}
        </p>
      )}

      {sched && (
        <div className="mt-3 flex items-center justify-between gap-2 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2" data-testid={`dr-scheduled-sms-${req.id}`}>
          <span className="inline-flex items-center gap-1.5 text-sm text-blue-700 font-medium">
            <Clock size={14} /> Dispatch scheduled to post at {schedTime}
          </span>
          {canApprove && (
            <button
              onClick={() => onCancelSms(req)}
              className="text-xs font-semibold text-red-600 hover:text-red-700 hover:underline"
              data-testid={`dr-cancel-sms-${req.id}`}
            >
              Cancel
            </button>
          )}
        </div>
      )}

      {!sched && req.status !== 'cancelled' && Array.isArray(req.dispatch_recipients) && req.dispatch_recipients.length > 0 && (
        <div className="mt-3 pt-3 border-t border-gray-100" data-testid={`dr-dispatch-status-${req.id}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-[#1e3a5f] uppercase tracking-wide flex items-center gap-1.5">
              <MessageSquare size={13} /> Dispatch chat status
            </p>
            <a href={`/dispatch-chat?request=${req.id}`} className="text-xs font-semibold text-blue-700 hover:underline" data-testid={`dr-open-chat-${req.id}`}>Open job chat →</a>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {req.dispatch_recipients.map((rec, i) => (
              <DispatchStatusChip key={rec.crew_member_id || rec.crew_member_phone || i} rec={rec} />
            ))}
          </div>
        </div>
      )}

      {(req.status === 'pending') && (canApprove || isOwner) && (
        <div className="mt-4 flex flex-wrap gap-2 pt-3 border-t border-gray-100">
          {canApprove && (
            <>
              <button onClick={() => onApprove(req)} className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-approve-btn-${req.id}`}>
                <CheckCircle2 size={15} /> Approve
              </button>
              <button onClick={() => onDecline(req)} className="inline-flex items-center gap-1.5 bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-decline-btn-${req.id}`}>
                <XCircle size={15} /> Decline
              </button>
            </>
          )}
          {isOwner && (
            <>
              <button onClick={() => onEdit(req)} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-edit-btn-${req.id}`}>
                <Pencil size={15} /> Edit
              </button>
              <button onClick={() => onDelete(req)} className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-delete-btn-${req.id}`}>
                <Trash2 size={15} /> Delete
              </button>
            </>
          )}
        </div>
      )}

      {req.status === 'approved' && canApprove && (
        <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
          <button onClick={() => onModifyCrew(req)} className="inline-flex items-center gap-1.5 bg-white border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#eef3f9] text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-modify-crew-btn-${req.id}`}>
            <Users size={15} /> Modify Crew
          </button>
          <button onClick={() => onEdit(req)} title="Change dates, times, site, contact, PO#, equipment, tools or scope" className="inline-flex items-center gap-1.5 bg-white border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#eef3f9] text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-edit-details-btn-${req.id}`}>
            <Pencil size={15} /> Edit Details
          </button>
          <button onClick={() => onSendText(req)} className="inline-flex items-center gap-1.5 bg-white border border-green-600 text-green-700 hover:bg-green-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-send-text-btn-${req.id}`}>
            <MessageSquare size={15} /> Dispatch / Re-post
          </button>
          <a href={`/dispatch-chat?request=${req.id}`} className="inline-flex items-center gap-1.5 bg-white border border-blue-300 text-blue-700 hover:bg-blue-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-job-chat-btn-${req.id}`}>
            <MessageSquare size={15} /> Job Chat
          </a>
          {onRateJob && req.start_date <= new Date().toISOString().slice(0, 10) && (
            <button onClick={() => onRateJob(req)} title="Rate how each guy did — MASON turns it into star suggestions for the office" className="inline-flex items-center gap-1.5 bg-white border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-rate-job-btn-${req.id}`}>
              <Star size={15} /> Rate how it went
            </button>
          )}
          <button onClick={() => onCancelJob(req)} className="inline-flex items-center gap-1.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-cancel-job-btn-${req.id}`}>
            <XCircle size={15} /> Cancel Dispatch
          </button>
          <button onClick={() => onDelete(req)} title="Remove this request and its schedule bars without texting anyone — for tests and duplicates" className="inline-flex items-center gap-1.5 bg-white border border-gray-200 text-gray-500 hover:bg-gray-50 text-sm font-medium px-3.5 py-2 rounded-lg ml-auto" data-testid={`dr-delete-btn-${req.id}`}>
            <Trash2 size={15} /> Delete (no texts)
          </button>
        </div>
      )}

      {req.status === 'cancelled' && canApprove && !req.rebooked_to && (
        <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
          <button onClick={() => onRebook(req)} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-rebook-btn-${req.id}`}>
            <Calendar size={15} /> Re-book Job
          </button>
        </div>
      )}

      {req.status === 'approved' && ((Array.isArray(req.tools) && req.tools.length > 0) || (req.tool_amendments || []).length > 0) && (canApprove || isOwner) && (
        <div className="mt-4 flex flex-wrap items-center gap-2 pt-3 border-t border-gray-100">
          <button onClick={() => onPrintSheet(req)} className="inline-flex items-center gap-1.5 bg-white border border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#eef3f9] text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-print-sheet-${req.id}`}>
            <Printer size={15} /> Print Tool Sheet
          </button>
          <button onClick={async () => {
              try {
                const r = await axios.post(`${API}/tool-crib/kits`, { dispatch_request_id: req.id, work_order_number: req.work_order_number }, authHeader());
                window.location.assign(`/tool-crib?kit=${r.data.id}`);
              } catch (e) { toast.error(e.response?.data?.detail || 'Could not open the kit'); }
            }} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-open-kit-${req.id}`} title="Open this job's kit in the Tool Crib (pack / unpack on the tablet)">
            <Wrench size={15} /> Job Kit
          </button>
          {canApprove && (
            <button onClick={() => onLogLoaded(req)} className="inline-flex items-center gap-1.5 bg-[#1e3a5f] hover:bg-[#16304f] text-white text-sm font-medium px-3.5 py-2 rounded-lg" data-testid={`dr-log-loaded-${req.id}`}>
              <ClipboardCheck size={15} /> {req.loaded_tools?.length ? 'Edit Loaded' : 'Log Loaded'}
            </button>
          )}
          {Array.isArray(req.loaded_tools) && req.loaded_tools.length > 0 && (() => {
            const short = req.loaded_tools.filter((l) => (l.qty_picked || 0) < (l.required_qty || 0));
            const done = short.length === 0;
            return (
              <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md ${done ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid={`dr-loaded-summary-${req.id}`}>
                {done ? <CheckCircle2 size={13} /> : <AlertTriangle size={13} />}
                {done ? `All ${req.loaded_tools.length} tools loaded` : `${short.length} of ${req.loaded_tools.length} short`}
                {req.loaded_by_name ? ` · ${req.loaded_by_name}` : ''}
              </span>
            );
          })()}
        </div>
      )}
    </div>
  );
};

// ── Main page ───────────────────────────────────────────────────────
const DispatchRequests = () => {
  const { user } = useAuth();
  const canApprove = APPROVE_ROLES.includes(user?.role);

  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);
  const [requests, setRequests] = useState([]);
  const [resources, setResources] = useState({ crew: [], trucks: [], trailers: [], welders: [], forklifts: [] });
  const [loading, setLoading] = useState(true);
  const initialQ = new URLSearchParams(window.location.search).get('q') || '';
  const [filter, setFilter] = useState(initialQ ? 'all' : (canApprove ? 'pending' : 'all'));
  const [search, setSearch] = useState(initialQ);
  const [approveReq, setApproveReq] = useState(null);
  const [declineReq, setDeclineReq] = useState(null);
  const [smsReq, setSmsReq] = useState(null);
  const [loadedReq, setLoadedReq] = useState(null);
  const [modifyReq, setModifyReq] = useState(null);
  const [amendReq, setAmendReq] = useState(null); // { req, amendment|null }
  const [smsRestrict, setSmsRestrict] = useState(null);
  const [smsPick, setSmsPick] = useState(false);
  const [cancelReq, setCancelReq] = useState(null);
  const [rebookReq, setRebookReq] = useState(null);
  const [rateReq, setRateReq] = useState(null);
  const canRate = ['super_admin', 'admin', 'office_admin', 'dispatch', 'project_manager', 'project_manager_estimator', 'millwright_foreman'].includes(user?.role);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [rRes, resRes] = await Promise.all([
        axios.get(`${API}/dispatch/requests`, authHeader()),
        axios.get(`${API}/dispatch/resources`, authHeader()),
      ]);
      setRequests(rRes.data || []);
      setResources(resRes.data || { crew: [], trucks: [], trailers: [], welders: [], forklifts: [] });
    } catch (e) {
      if (!silent) toast.error('Failed to load dispatch requests');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Poll quietly so the dispatch-text status (hourglass → check/X) stays live as crew reply
  useEffect(() => {
    const t = setInterval(() => { load(true); }, 30000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  const del = async (req) => {
    const approved = req.status === 'approved';
    if (!window.confirm(approved
      ? `Delete approved request ${req.request_number}?\n\nThis removes the request and its bars from the schedule and cancels any scheduled text. Nobody on the crew is messaged.`
      : `Delete request ${req.request_number}?`)) return;
    try {
      await axios.delete(`${API}/dispatch/requests/${req.id}`, authHeader());
      toast.success(approved ? 'Request deleted — no texts sent' : 'Request deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const cancelSms = async (req) => {
    const schedId = req.scheduled_sms?.id;
    if (!schedId) return;
    if (!window.confirm('Cancel the scheduled dispatch?')) return;
    try {
      await axios.post(`${API}/dispatch/scheduled/${schedId}/cancel`, {}, authHeader());
      toast.success('Scheduled dispatch cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const clearSlot = async (req) => {
    if (!window.confirm('Clear this unfilled slot? The request will stop asking for that extra crew member.')) return;
    try {
      const r = await axios.post(`${API}/dispatch/requests/${req.id}/clear-slot`, {}, authHeader());
      toast.success(r.data?.unfilled_left > 0
        ? `Slot cleared — ${r.data.unfilled_left} still open`
        : 'Slot cleared — crew is set');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to clear slot'); }
  };

  const filtered = useMemo(() => {
    let rows = requests;
    if (filter !== 'all') rows = rows.filter((r) => r.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((r) =>
        [r.request_number, r.customer_name, r.job_site, r.work_order_number, r.created_by_name, r.description]
          .filter(Boolean).some((v) => v.toLowerCase().includes(q))
      );
    }
    return rows;
  }, [requests, filter, search]);

  const pendingCount = requests.filter((r) => r.status === 'pending').length;

  if (view === 'create') {
    return (
      <RequestForm
        resources={resources}
        initial={editing}
        onCancel={() => { setView('list'); setEditing(null); }}
        onSaved={() => { setView('list'); setEditing(null); load(); }}
      />
    );
  }

  const FILTERS = canApprove
    ? [['pending', `Pending${pendingCount ? ` (${pendingCount})` : ''}`], ['approved', 'Approved'], ['declined', 'Declined'], ['cancelled', 'Cancelled'], ['all', 'All']]
    : [['all', 'All'], ['pending', 'Pending'], ['approved', 'Approved'], ['declined', 'Declined'], ['cancelled', 'Cancelled']];

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="dispatch-requests-page">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#1e3a5f] p-2.5 rounded-lg"><Send className="text-white" size={22} /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dispatch Requests</h1>
            <p className="text-sm text-blue-800">{canApprove ? 'Review crew & equipment requests and add them to the schedule' : 'Request crew and equipment for your jobs'}</p>
          </div>
        </div>
        <button onClick={() => { setEditing(null); setView('create'); }} className="inline-flex items-center gap-2 bg-[#1e3a5f] hover:bg-[#16304f] text-white px-4 py-2.5 rounded-lg font-medium transition-colors" data-testid="dr-new-btn">
          <Plus size={18} /> New Request
        </button>
      </div>

      <div className="mb-4"><UnpackedBoxesBanner compact /></div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1.5 bg-gray-100 p-1 rounded-lg">
          {FILTERS.map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)}
              className={`px-3.5 py-1.5 rounded-md text-sm font-medium transition-colors ${filter === key ? 'bg-white text-[#1e3a5f] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              data-testid={`dr-filter-${key}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…" className="pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg w-56 focus:ring-2 focus:ring-[#1e3a5f] outline-none" data-testid="dr-search" />
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-slate-500"><Loader2 className="animate-spin mr-2" /> Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-xl border border-gray-200" data-testid="dr-empty">
          <Send className="mx-auto text-slate-700 mb-3" size={44} />
          <p className="text-gray-500">No requests here yet.</p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="dr-list">
          {filtered.map((req) => (
            <RequestCard
              key={req.id}
              req={req}
              canApprove={canApprove}
              isOwner={req.created_by === user?.id}
              userId={user?.id}
              onAmend={(r, a) => setAmendReq({ req: r, amendment: a })}
              onAmendmentsChanged={(id, list) => setRequests((prev) => prev.map((x) => (x.id === id ? { ...x, tool_amendments: list } : x)))}
              onApprove={setApproveReq}
              onDecline={setDeclineReq}
              onEdit={(r) => { setEditing(r); setView('create'); }}
              onDelete={del}
              onCancelSms={cancelSms}
              onPrintSheet={(r) => printToolSheet(r.id)}
              onLogLoaded={setLoadedReq}
              onModifyCrew={setModifyReq}
              onSendText={(r) => { setSmsRestrict(null); setSmsPick(true); setSmsReq(r); }}
              onCancelJob={setCancelReq}
              onRebook={setRebookReq}
              onClearSlot={clearSlot}
              onRateJob={canRate ? setRateReq : undefined}
            />
          ))}
        </div>
      )}

      {amendReq && (
        <ToolAmendmentModal req={amendReq.req} amendment={amendReq.amendment} onClose={() => setAmendReq(null)}
          onSaved={(list) => setRequests((prev) => prev.map((x) => (x.id === amendReq.req.id ? { ...x, tool_amendments: list } : x)))} />
      )}
      {approveReq && (
        <ApproveModal
          req={approveReq}
          resources={resources}
          onClose={() => setApproveReq(null)}
          onApproved={(approved) => { setApproveReq(null); load(); if (approved) setSmsReq(approved); }}
        />
      )}
      {declineReq && (
        <DeclineModal req={declineReq} onClose={() => setDeclineReq(null)} onDeclined={() => { setDeclineReq(null); load(); }} />
      )}
      {smsReq && (
        <DispatchSmsModal
          req={smsReq}
          restrictTo={smsRestrict}
          pickRecipients={smsPick}
          onSkip={() => { setSmsReq(null); setSmsRestrict(null); setSmsPick(false); }}
          onDone={() => { setSmsReq(null); setSmsRestrict(null); setSmsPick(false); load(); }}
        />
      )}
      {rateReq && <RateJobModal req={rateReq} onClose={() => setRateReq(null)} />}
      {modifyReq && (
        <ModifyCrewModal
          req={modifyReq}
          resources={resources}
          onClose={() => setModifyReq(null)}
          onSaved={(data) => {
            setModifyReq(null);
            load();
            if (data?.added?.length && data?.texts_sent_before) {
              setSmsRestrict(data.added);
              setSmsReq(data.request);
            }
          }}
        />
      )}
      {loadedReq && (
        <LoadedToolsModal
          req={loadedReq}
          onClose={() => setLoadedReq(null)}
          onSaved={() => { setLoadedReq(null); load(); }}
        />
      )}
      {cancelReq && (
        <CancelJobModal
          requestId={cancelReq.id}
          onClose={() => setCancelReq(null)}
          onCancelled={() => { setCancelReq(null); load(); }}
        />
      )}
      {rebookReq && (
        <RebookJobModal
          requestId={rebookReq.id}
          jobLabel={`WO ${rebookReq.work_order_number || '—'}${rebookReq.customer_name ? ` — ${rebookReq.customer_name}` : ''} · was ${fmtDate(rebookReq.start_date)}${rebookReq.end_date && rebookReq.end_date !== rebookReq.start_date ? ` – ${fmtDate(rebookReq.end_date)}` : ''}`}
          onClose={() => setRebookReq(null)}
          onRebooked={(d) => {
            setRebookReq(null);
            load();
            if (d?.request) { setSmsRestrict(null); setSmsPick(true); setSmsReq(d.request); }
          }}
        />
      )}
    </div>
  );
};

export default DispatchRequests;
